<script setup lang="ts">
	import autocomplete from "autocompleter";

	import type { IValue } from "~/interfaces";
	import ErrorMessage from "~/components/ui/ErrorMessage.vue";

	defineComponent({
		name: "AutoComplete",
	});

	const { searchFunction, clearIncomplete, uppercase } = defineProps({
		name: { type: String, required: false },
		label: { type: String, default: "", required: true },
		disabled: { type: Boolean, default: false },
		searchFunction: { type: Function, required: true },
		error: { type: Boolean, default: false },
		errorMessage: { type: String, default: "" },
		clearIncomplete: { type: Boolean, default: false, required: false },
		uppercase: { type: Boolean, default: true },
		testId: { type: String, default: "" },
		type: { type: String, default: "text" },
	});

	const value = defineModel({
		type: String,
		default: "",
	});

	const uid = getCurrentInstance()?.uid;

	const inputRef = ref<HTMLInputElement | null>(null);
	const listRef = ref<HTMLDivElement | undefined>();
	const match = ref<boolean>(false);
	const select = ref<boolean>(false);
	const inputValue = ref<string>("");

	const emit = defineEmits(["input"]);

	watch(value, (newValue) => {
		inputValue.value = newValue;
		emit("input", newValue);
	});

	watch(inputValue, (newValue) => {
		value.value = newValue;
		emit("input", newValue);
	});

	onMounted(() => {
		if (inputRef.value) {
			inputValue.value = value.value;

			const engine = autocomplete({
				input: inputRef.value,

				debounceWaitMs: 200,

				fetch: function (term: string, update: (data: any[]) => void) {
					select.value = false;
					const results = searchFunction(term);
					match.value = results.length > 0;
					update(results);
				},

				container: listRef.value,

				render: function (item, currentValue) {
					const div = document.createElement("div");

					let highlightedSuggestion = currentValue;

					if (currentValue.includes("@")) {

						const [inputName, inputDomainWithExt] = currentValue.split("@");

						const suggestedDomainWithExtension = item.split("@")[1];

						const lastDotIndex = suggestedDomainWithExtension.lastIndexOf(".");
						const suggestedDomain = lastDotIndex !== -1 ? suggestedDomainWithExtension.slice(0, lastDotIndex) : suggestedDomainWithExtension;
						const suggestedExtension = lastDotIndex !== -1 ? suggestedDomainWithExtension.slice(lastDotIndex + 1) :"";

						if (inputDomainWithExt) {

							const lastDotIndex = inputDomainWithExt.lastIndexOf(".");
							const isolatedDomain = lastDotIndex !== -1 ? inputDomainWithExt.slice(0, lastDotIndex) : inputDomainWithExt;
						
							const matchedDomainPart = suggestedDomain.startsWith(isolatedDomain) ? isolatedDomain : "";

							const correctedDomainPart = matchedDomainPart ? suggestedDomain.slice(matchedDomainPart.length) : suggestedDomain;

							highlightedSuggestion = `<b>${inputName}@</b><b>${matchedDomainPart}</b>${matchedDomainPart ? correctedDomainPart : suggestedDomain }.${suggestedExtension}`;

						} else {

							highlightedSuggestion = `<b>${currentValue}</b>${item.slice(currentValue.length)}`;

						}

					} else {

						highlightedSuggestion = `<b>${currentValue}</b>${item.slice(currentValue.length)}`;

					}

					div.innerHTML = highlightedSuggestion;
					div.role = "option";
					div.classList.add("autocomplete-suggestion");
					uppercase && div.classList.add("uppercase");
					return div;
				},

				onSelect: function (term) {
					select.value = true;
					inputValue.value = term;
					value.value = term;
					emit("input", term);
				},

				preventSubmit: 1,
			});

			onBeforeUnmount(() => {
				engine.destroy();
			});
		}
	});

	const handleBlur = (e: FocusEvent) => {
		if (!clearIncomplete) {
			value.value = inputValue.value;
		} else {
			if (inputValue.value !== value.value || !match.value || !select.value) {
				value.value = "";
				inputValue.value = "";
			}
		}
	};

	const handleInput = () => {
		formatInput();
		if (!clearIncomplete) {
			inputValue.value = inputRef.value?.value || "";
			value.value = inputValue.value;
			emit("input", inputValue.value);
		}
	};

	const formatInput = () => {
		inputRef.value!.value = inputRef.value?.value.replace(/’/g, "'") ?? "";
	};
</script>

<template>
	<div class="label-floating relative" :class="{ 'border-red': error }">
		<input
			:id="`autocomplete-${uid}`"
			ref="inputRef"
			v-model="inputValue"
			:type="type"
			:placeholder="label"
			:disabled="disabled"
			:class="['autocomplete-input primary', uppercase ? 'uppercase' : '']"
			@blur="handleBlur"
			@input="handleInput"
			role="combobox"
			aria-autocomplete="list"
			aria-haspopup="listbox"
			:aria-expanded="!!select"
			:aria-owns="`autocomplete-list-${uid}`"
			autocomplete="off"
		/>
		<label :for="`autocomplete-${uid}`">{{ label }}</label>
		<div class="relative">
			<div
				class="autocomplete-suggestions"
				ref="listRef"
				:id="`autocomplete-list-${uid}`"
				role="listbox"
			></div>
		</div>
	</div>
	<ErrorMessage v-if="error" :message="errorMessage" />
</template>

<style scoped>
	.autocomplete-input {
		@apply sm:h-12 placeholder:normal-case;
	}
</style>
